import { styled } from "@mui/material/styles";
import { ReactElement } from "react";
import {
  Drawer,
  DrawerProps,
  useScrollTrigger,
  useMediaQuery,
  Theme
} from "@mui/material";
// import { useLocale } from "ra-core";
import { useSidebarState } from "react-admin";

export const CustomSideBar = (props: SidebarProps) => {
  const { children, closedSize, size, ...rest } = props;
  const isSmall = useMediaQuery<Theme>("(max-width:640px)");
  const isMedium = useMediaQuery<Theme>("(max-width:1024px)");
  const isIpadSizeScreen = isMedium && !isSmall;
  const [open, setOpen] = useSidebarState();
  // useLocale();
  // force redraw on locale change
  const trigger = useScrollTrigger();

  const toggleSidebar = () => setOpen(!open);

  return isSmall ? (
    <StyledDrawer
      variant="temporary"
      open={open}
      isIpadSizeScreen={isIpadSizeScreen}
      onClose={toggleSidebar}
      classes={SidebarClasses}
      sx={{
        top: "64px",
        "& .MuiBackdrop-root": {
          top: "64px"
        },
        "& .RaSidebar-paper": {
          top: "64px",
          position: "inherit",
          width: "100%"
        },
        "& .RaMenu-open": {
          width: "100% !important"
        }
      }}
      {...rest}
    >
      {children}
    </StyledDrawer>
  ) : (
    <StyledDrawer
      variant="permanent"
      open={open}
      onClose={toggleSidebar}
      classes={SidebarClasses}
      className={trigger ? SidebarClasses.appBarCollapsed : ""}
      {...rest}
      sx={{
        "& .MuiPaper-root": {
          width: open
            ? isIpadSizeScreen
              ? IPAD_SIZE_SCREEN_DRAWER_WIDTH
              : DRAWER_WIDTH
            : CLOSED_DRAWER_WIDTH
        }
      }}
    >
      <div className={SidebarClasses.fixed}>{children}</div>
    </StyledDrawer>
  );
};

export interface SidebarProps extends DrawerProps {
  children: ReactElement;
  closedSize?: number;
  isIpadSizeScreen?: boolean;
  size?: number;
}

const PREFIX = "RaSidebar";

export const SidebarClasses = {
  docked: `${PREFIX}-docked`,
  paper: `${PREFIX}-paper`,
  paperAnchorLeft: `${PREFIX}-paperAnchorLeft`,
  paperAnchorRight: `${PREFIX}-paperAnchorRight`,
  paperAnchorTop: `${PREFIX}-paperAnchorTop`,
  paperAnchorBottom: `${PREFIX}-paperAnchorBottom`,
  paperAnchorDockedLeft: `${PREFIX}-paperAnchorDockedLeft`,
  paperAnchorDockedTop: `${PREFIX}-paperAnchorDockedTop`,
  paperAnchorDockedRight: `${PREFIX}-paperAnchorDockedRight`,
  paperAnchorDockedBottom: `${PREFIX}-paperAnchorDockedBottom`,
  modal: `${PREFIX}-modal`,
  fixed: `${PREFIX}-fixed`,
  appBarCollapsed: `${PREFIX}-appBarCollapsed`
};

const StyledDrawer = styled(Drawer, {
  name: PREFIX,
  slot: "Root",
  overridesResolver: (props, styles) => styles.root,
  shouldForwardProp: () => true
})(({ open, theme }) => ({
  height: "100vh",
  marginTop: 0,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen
  }),
  [`&.${SidebarClasses.appBarCollapsed}`]: {
    // compensate the margin of the Layout appFrame instead of removing it in the Layout
    // because otherwise, the appFrame content without margin may revert the scrollTrigger,
    // leading to a visual jiggle
    marginTop: theme.spacing(-6),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(-7)
    },
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  [`& .${SidebarClasses.docked}`]: {},
  [`& .${SidebarClasses.paper}`]: {},
  [`& .${SidebarClasses.paperAnchorLeft}`]: {},
  [`& .${SidebarClasses.paperAnchorRight}`]: {},
  [`& .${SidebarClasses.paperAnchorTop}`]: {},
  [`& .${SidebarClasses.paperAnchorBottom}`]: {},
  [`& .${SidebarClasses.paperAnchorDockedLeft}`]: {},
  [`& .${SidebarClasses.paperAnchorDockedTop}`]: {},
  [`& .${SidebarClasses.paperAnchorDockedRight}`]: {},
  [`& .${SidebarClasses.paperAnchorDockedBottom}`]: {},
  [`& .${SidebarClasses.modal}`]: {},

  [`& .${SidebarClasses.fixed}`]: {
    position: "fixed",
    top: "0px",
    left: "0px",
    height: "100vh",
    minWidth: CLOSED_DRAWER_WIDTH,
    overflow: "hidden",
    // hide scrollbar
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none"
    }
  },

  [`& .MuiPaper-root`]: {
    position: "relative",
    // width: open
    //   ? isIpadSizeScreen
    //     ? IPAD_SIZE_SCREEN_DRAWER_WIDTH
    //     : DRAWER_WIDTH
    //   : CLOSED_DRAWER_WIDTH,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    backgroundColor: "transparent",
    borderRight: "none",
    [theme.breakpoints.only("xs")]: {
      marginTop: 0,
      height: "100vh",
      position: "inherit",
      backgroundColor: theme.palette.background.default
    },
    [theme.breakpoints.up("md")]: {
      border: "none"
    },
    zIndex: "inherit"
  }
}));

export const DRAWER_WIDTH = 248;
export const IPAD_SIZE_SCREEN_DRAWER_WIDTH = 190;
export const CLOSED_DRAWER_WIDTH = 64;
